import {Component, OnInit, QueryList, ViewChildren} from "@angular/core";
import {AbstractControl, FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {PanelMountRegistrationStepRoutesBack} from "../../data/panel-mount-registration-step-routes";
import {WarrantyLru} from "../../json/warranty-lru";
import {PanelMountRegistrationManager} from "../../services/panel-mount-registration-manager";
import {PanelMountRegistrationLruRowComponent, SerialNumStatus} from "./sub-components/panel-mount-registration-lru-row.component";

/**
 * Describes why the continue button is disabled
 */
enum ContinueBtnDisabledError {
	LRUS_PURCHASE_DATE_REQUIRED = "Please enter an install date for LRUs.",
	SERIAL_NUM_REQUIRED = "At least one device serial number is required for registration.",
	LOADING_SERIAL_NUMBERS = "Please wait for all device serial numbers to finish verification.",
	INVALID_SERIAL_NUMBERS = "Please remove all invalid device serial numbers before continuing.",
	LRU_INPUT_FIELD_NONEMPTY = "You still have data in the input field that needs to be verified.",
	VALID = "",
}

@Component({
	selector: "panel-mount-registration-lru-serial-numbers",
	template: `
		<!---------- ENTER LRU SERIAL NUMBERS ---------->
		<form [formGroup]="panelFormGroup">

			<!---------- HEADER ---------->
			<div class="warrantyregistration_panel_header">
				<h2>{{ (existingLRUs) ? "Add" : "" }} LRU Serial Numbers</h2>
			</div>

			<!---------- LRUS INSTALL DATE ---------->
			<p>
				Please enter an install date for the LRUs.
			</p>
			<div class="form_field" style="margin: 20px 0 50px 0;">
				<label for="lrusPurchaseDate">Install Date for LRUs<span>*</span></label>
				<input
					type="date"
					[(ngModel)]="manager.lrusPurchaseDate"
					id="lrusPurchaseDate"
					class="install_date mobile-input"
					formControlName="lrusPurchaseDate"
					[max]="maxInstallDateString">
				<form-errors [control]="lrusPurchaseDate" [messages]="maxDateErrorMessage"></form-errors>
			</div>

			<!---------- HIDE EVERYTHING BELOW IF LRUS INSTALL DATE HAS NOT BEEN ENTERED ---------->
			<ng-container *ngIf="hasValidLrusPurchaseDate">

				<!---------- INSTRUCTIONS ---------->
				<p class="warrantyregistration_panel_header_desc-text">
					Please enter the serial number for each {{ (existingLRUs) ? "additional" : "installed" }} LRU (Line Replaceable Unit).
				</p>

				<!---------- EXISTING LRUs - Show/Hide Button ---------->
				<div *ngIf="existingLRUs" style="margin-top: 20px;">
					<button class="button button-secondary" (click)="toggleShowingExistingLRUs()">
						{{ (showExistingLRUs) ? "Hide" : "Show"}} {{ existingLRUs.length }} Already-Registered LRUs
					</button>
				</div>

				<!---------- EXISTING LRUs ---------->
				<div *ngIf="showExistingLRUs" class="warrantyregistration_panel_existingdetails">
						<h3>Registered LRUs</h3>
						<panel-mount-registration-lrus-table [lrus]="existingLRUs">
						</panel-mount-registration-lrus-table>
				</div>

				<div class="warrantyregistration_panel_lru">

					<!---------- INPUT LRU SERIAL NUMBERS ---------->
					<div class="warrantyregistration_panel_lru_input">
						<h2>Input {{ (existingLRUs) ? "Additional" : "" }} Serial Numbers</h2>

						<!---------- INSTRUCTIONS ---------->
						<p class="warrantyregistration_panel_lru_input-header-instruction">
							Enter each serial number on a separate line.
						</p>

						<!---------- SERIAL NUMBER INPUT ---------->
						<textarea class="warrantyregistration_panel_lru_input-textarea" rows="8"
							id="lruInput" formControlName="lruInput"></textarea>

						<!---------- VERIFY SERIAL NUMBERS BUTTON ---------->
						<div class="warrantyregistration_panel_lru_input-button">
							<button type="button" (click)="parseLruSerialNumbers()" [disabled]="!panelFormGroup.valid"
								class="button button-primary button-large">Verify Serial Numbers</button>
						</div>
					</div>

					<!---------- SERIAL NUMBER ROWS ---------->
					<div class="warrantyregistration_panel_lru_row-container">
						<h2>Verified LRUs</h2>

						<hr class="warrantyregistration_panel_row_divider">
						<div *ngFor="let serialNumObj of lruSerialNums; index as i">
							<panel-mount-registration-lru-row [idx]="i" [lruSerialNumber]="serialNumObj.serialNumber"
								[verifiedLru]="serialNumObj.starterLru" (deleteEmitter)="removeLru($event)"
								(checkForDuplicatesEmitter)="checkForDuplicates($event)" (updatedSerialNumEmitter)="updateSerialNum($event)">
							</panel-mount-registration-lru-row>
						</div>
					</div>
				</div>

			</ng-container>

			<!---------- FOOTER ---------->
			<div class="warrantyregistration_panel_inputlrus_footer">

				<!---------- NAVIGATION BUTTONS ---------->
				<div class="warrantyregistration_panel_buttons">
					<button type="button" [routerLink]="panelMountRegistrationStepRoutes.STEP_TWO" class="button button-secondary button-large">Previous</button>
					<div>
						<button type="submit" class="button button-primary button-large" [disabled]="continueBtnDisabled()" (click)="continueFunc()">Continue</button>
					</div>
				</div>

				<!---------- TOOLTIP ---------->
				<p class="warrantyregistration_panel_lru_error statuscolor-red">
					{{ continueBtnDisabledErrorStr }}
				</p>
			</div>
		</form>
	`,
})
export class PanelMountRegistrationLruSerialNumbersComponent implements OnInit {
	@ViewChildren(PanelMountRegistrationLruRowComponent) rows: QueryList<PanelMountRegistrationLruRowComponent>; // References to all LRU rows
	panelFormGroup: FormGroup;
	panelMountRegistrationStepRoutes = PanelMountRegistrationStepRoutesBack;
	lruSerialNums: Array<{serialNumber: string, status: SerialNumStatus, starterLru: WarrantyLru}> = []; // List of serial numbers, their current status, and a starterLru (if they have one)
	continueBtnDisabledErrorTooltip = ""; // Tooltip to explain why the continue button is disabled
	tooltipTimer: NodeJS.Timer; // Timer that controls when the tooltip is shown
	continueBtnDisabledErrorStr = ContinueBtnDisabledError.VALID; // Explains why continue button is disabled
	existingLRUs: Array<WarrantyLru>;
	showExistingLRUs: boolean;
	maxInstallDate: Date = new Date();
	maxInstallDateString: string;
	maxDateErrorMessage = {
		maxDateExceeded: "Date cannot be more than 2 weeks in the future"
	};

	constructor(
		public manager: PanelMountRegistrationManager,
		private router: Router,
		private route: ActivatedRoute,
	) {
		// Fill the lruSerialNums with preexisting LRUs
		this.manager.lrus.forEach(lru => {
			this.lruSerialNums.push({
				serialNumber: lru.serialNumber,
				status: SerialNumStatus.VALID,
				starterLru: lru,
			});
		});
		this.maxInstallDate.setDate(this.maxInstallDate.getDate() + 14);
		this.maxInstallDateString = this.maxInstallDate.toISOString().split('T')[0];
	}

	ngOnInit() {
		this.panelFormGroup = this.createFormGroup();
		this.continueBtnDisabled();
		this.existingLRUs = this.manager.existingLRUs;
	}

	toggleShowingExistingLRUs() {
		this.showExistingLRUs = !this.showExistingLRUs;
	}

	// Creates the form group that contains all form information:
	//  LRU Input Field
	//  @return the generated form group
	createFormGroup(): FormGroup {
		return new FormGroup({
			lruInput: new FormControl(),
			lrusPurchaseDate: new FormControl(
				"", [
					Validators.required,
					Validators.pattern("(\\d\\d\\d\\d)-(\\d\\d)-(\\d\\d)"),
					this.maxDateValidator
				]),
		});
	}

	// Getters for form elements
	get lruInput() { return this.panelFormGroup.get("lruInput"); }
	get lrusPurchaseDate() { return this.panelFormGroup.get("lrusPurchaseDate"); }

	get hasValidLrusPurchaseDate(): boolean {
		return this.lrusPurchaseDate.valid;
	}

	parseLruSerialNumbers() {
		if (!this.lruInput.value || this.lruInput.value === "")
			return;

		let serialNums = this.lruInput.value.split("\n");
		this.lruInput.setValue("");
		for (let serialNum of serialNums) {
			serialNum = serialNum.trim();
			if (serialNum !== "") {
				this.lruSerialNums.push({
					serialNumber: serialNum,
					status: SerialNumStatus.LOADING,
					starterLru: null,
				});
			}
		}
	}

	// Removes an existing LRU
	//  @param index Index of LRU
	removeLru(index: number) {
		if (this.lruSerialNums[index].status === SerialNumStatus.VALID) { // If we are removing a verified LRU row, remove the verified LRU from registration manager
			const serialNum = this.lruSerialNums[index].serialNumber;
			this.manager.lrus = this.manager.lrus.filter(lru => lru.serialNumber !== serialNum);
		}

		// Remove the row
		this.lruSerialNums = this.lruSerialNums.filter((_, i) => i !== index);
	}

	// Checks for any duplicates of a given serial number
	//  @param obj Contains the index of the serial number row to check and whether or not the serial number is fresh
	//           If freshLru is true, the serial number has just been created. If false, we are editing it
	//           This is important because if it is fresh, we only want to check for duplicates that have been entered before this one, none after
	//           (This avoids deadlock where both identical serial numbers are invalid, instead of just one)
	checkForDuplicates(obj: {index: number, freshLru: boolean}) {
		const lruRowRef = this.rows.toArray()[obj.index]; // Reference to the LRU row
		const serialNum = lruRowRef.lruSerialNumber;
		let isDuplicate = false;
		if (obj.freshLru) { // Check all previous serial numbers, but nothing past the index
			for (let i = 0; i < obj.index; i++) {
				if (this.lruSerialNums[i].serialNumber === serialNum) { isDuplicate = true; }
			}
		}
		else { // Check all serial numbers except for the index row
			isDuplicate = this.lruSerialNums.some((serialNumObj, i) => (i !== obj.index) && (serialNumObj.serialNumber === serialNum));
		}
		lruRowRef.duplicateCallback(isDuplicate); // Call the callback of the child
	}

	// Updates the lruSerialNums array whenever an LRU row updates its state
	//  @param obj Contains the index of the serial number row that is updated & the state of that row
	updateSerialNum(obj: {index: number, status: SerialNumStatus}) {
		if (obj.status === SerialNumStatus.VALID) { // If we have just verified an LRU, add it to registration manager
			this.manager.lrus.push(this.rows.toArray()[obj.index].verifiedLru);
		}
		else if (obj.status === SerialNumStatus.LOADING) { // If we have just edited an LRU, remove it
			const serialNum = this.lruSerialNums[obj.index].serialNumber;
			this.manager.lrus = this.manager.lrus.filter(lru => lru.serialNumber !== serialNum);
		}

		// Update the serial number
		this.lruSerialNums[obj.index].serialNumber = this.rows.toArray()[obj.index].lruSerialNumber;
		this.lruSerialNums[obj.index].status = obj.status;
	}

	// Collects all verified LRUs from the input rows
	//  @return An array of all verified LRUs
	collectLrus(): Array<WarrantyLru> { return this.rows.filter(row => row.verifiedLru !== null).map(row => row.verifiedLru); }

	// Determines if the continue button should be disabled or enabled
	// Should only be enabled if we have at least one serial number, all LRUs are verified, and there is nothing in the lruInput field
	//  @return True if continue button should be disabled, false otherwise
	continueBtnDisabled(): boolean {
		if (!this.lrusPurchaseDate.valid) {
			this.continueBtnDisabledErrorStr = ContinueBtnDisabledError.LRUS_PURCHASE_DATE_REQUIRED;
			return true;
		}
		if (this.lruSerialNums.length === 0) { // Need at least one serial number
			this.continueBtnDisabledErrorStr = ContinueBtnDisabledError.SERIAL_NUM_REQUIRED;
			return true;
		}
		else if (this.lruSerialNums.some(lruObj => lruObj.status === SerialNumStatus.LOADING)) { // No loading serial numbers allowed
			this.continueBtnDisabledErrorStr = ContinueBtnDisabledError.LOADING_SERIAL_NUMBERS;
			return true;
		}
		else if (this.lruSerialNums.some(lruObj => lruObj.status === SerialNumStatus.INVALID)) { // No invalid serial numbers allowed
			this.continueBtnDisabledErrorStr = ContinueBtnDisabledError.INVALID_SERIAL_NUMBERS;
			return true;
		}
		else if (this.lruInput && this.lruInput.value && this.lruInput.value.trim()) { // LRU Input must be empty
			this.continueBtnDisabledErrorStr = ContinueBtnDisabledError.LRU_INPUT_FIELD_NONEMPTY;
			return true;
		}
		this.continueBtnDisabledErrorStr = ContinueBtnDisabledError.VALID;
		return false;
	}

	// Continue function to be run when continue button is pressed
	continueFunc() {
		this.router.navigate([PanelMountRegistrationStepRoutesBack.STEP_FOUR], {relativeTo: this.route});
	}

	maxDateValidator = (control: AbstractControl) => {
		return control.value != null && new Date(control.value) > this.maxInstallDate
			? { 'maxDateExceeded': this.maxInstallDate }:
			null;
	}
}
